// ─── Permission Constants ──────────────────────────────────────────────────────

/** All permissions that grant access to any settings area. Used by the SettingsLayout gate. */
export const SETTINGS_ACCESS_PERMISSIONS: string[] = [
  'billing:read-own',
  'carriers:access-read-only',
  'carriers:manage',
  'servicekeys:manage',
  'sites:access-read-only',
  'sites:manage',
  'spaces:access-read-only',
  'spaces:manage',
  'tenants:access-read-only',
  'tenants:manage',
  'users:access-read-only',
  'users:manage',
];

/** Permissions that gate access to site settings pages. */
export const SITE_SETTINGS_PERMISSIONS: string[] = ['sites:access-read-only', 'sites:manage'];

/**
 * Permissions that gate access to the space detail settings page.
 * Must match the `beforeLoad` guard in `settings_.spaces.$spaceId.tsx`.
 */
export const SPACE_DETAIL_ACCESS_PERMISSIONS: string[] = [
  'spaces:access-read-only',
  'spaces:manage',
  'tenants:access-read-only',
  'tenants:manage',
];

// ─── Settings navigation types ────────────────────────────────────────────────

export type SettingsNavigationTarget =
  { route: 'home' } | { route: 'site'; siteId: string } | { route: 'space'; spaceId: string };

export type SettingsNavigationResolution =
  { status: 'loading' } | { status: 'resolved'; target: SettingsNavigationTarget | null };

export interface SettingsScopeState {
  /** hasAnyPermission(SETTINGS_ACCESS_PERMISSIONS) — only meaningful for the layout variant. */
  canAccessSettings: boolean;
  /** hasAnyPermission(SITE_SETTINGS_PERMISSIONS) */
  canEnterSiteSettings: boolean;
  /** hasAnyPermission(SPACE_DETAIL_ACCESS_PERMISSIONS) */
  canEnterSpaceDetail: boolean;
  /** First site scope ID for site-only-scoped users; null otherwise. */
  firstSiteId: string | null;
  /** true for SettingsLayout (isReady / home fallback); false for SettingsIndexRedirect. */
  includeAccessFallback: boolean;
  /** Permissions ready (from usePermissions). */
  isReady: boolean;
  /** useUserScopes isLoading — user or scope data not yet settled. */
  isScopeLoading: boolean;
  /** True when the user has one or more SITE-level scopes and no SPACE / TENANT scope. */
  isSiteOnlyScoped: boolean;
  /** MasterContext siteId. */
  siteId: string | null;
  /** MasterContext spaceId. */
  spaceId: string | null;
}

// ─── Space detail navigation types ───────────────────────────────────────────

export type SpaceDetailNavigationTarget =
  | { replace: boolean; route: 'settings' }
  | { route: 'site'; siteId: string }
  | { route: 'space'; spaceId: string; tabSuffix: string };

export interface SpaceDetailState {
  /** hasAnyPermission(SPACE_DETAIL_ACCESS_PERMISSIONS) */
  canRenderSpaceDetail: boolean;
  /** Boolean(space) */
  hasSpace: boolean;
  /** error !== null */
  hasSpaceError: boolean;
  /**
   * skipNextScopeChange ref value at effect time.
   * When true, the scope-change branches (1a/1b/1c) are skipped.
   * Never suppresses the unavailability branch.
   */
  isScopeChangeSuppressed: boolean;
  /** useUserScopes isLoading. */
  isScopeLoading: boolean;
  /** True when the user has only site-level scopes. */
  isSiteOnlyScoped: boolean;
  /** Space fetch query isLoading. */
  isSpaceLoading: boolean;
  /** MasterContext siteId. */
  masterSiteId: string | null;
  /** MasterContext spaceId. */
  masterSpaceId: string | null;
  /** spaceId route param. */
  routeSpaceId: string;
  /** extractTabSuffix(location.pathname, `/settings/spaces/${routeSpaceId}`) */
  tabSuffix: string;
}

// ─── Settings navigation resolver ─────────────────────────────────────────────

/**
 * Pure, framework-free decision function for settings-area navigation.
 * Encodes the exact cascade shared by SettingsLayout and SettingsIndexRedirect.
 *
 * Cascade:
 * 1. (layout only) !isReady → loading; !canAccessSettings → home.
 * 2. isScopeLoading → loading.
 * 3. siteId + site perms → site detail.
 * 4. isSiteOnlyScoped → first site (when permitted) or stay; NEVER falls through to space.
 * 5. spaceId + space/tenant perms → space detail.
 * 6. → stay (null target).
 */
export function resolveSettingsNavigation(state: SettingsScopeState): SettingsNavigationResolution {
  const {
    canAccessSettings,
    canEnterSiteSettings,
    canEnterSpaceDetail,
    firstSiteId,
    includeAccessFallback,
    isReady,
    isScopeLoading,
    isSiteOnlyScoped,
    siteId,
    spaceId,
  } = state;

  if (includeAccessFallback) {
    if (!isReady) return { status: 'loading' };
    if (!canAccessSettings) return { status: 'resolved', target: { route: 'home' } };
  }
  if (isScopeLoading) return { status: 'loading' };
  if (siteId !== null && canEnterSiteSettings) {
    return { status: 'resolved', target: { route: 'site', siteId } };
  }
  if (isSiteOnlyScoped) {
    return { status: 'resolved', target: resolveSiteOnlyNavigationTarget(firstSiteId, canEnterSiteSettings) };
  }
  if (spaceId !== null && canEnterSpaceDetail) {
    return { status: 'resolved', target: { route: 'space', spaceId } };
  }
  return { status: 'resolved', target: null };
}

// ─── Space detail navigation resolver ─────────────────────────────────────────

/**
 * Returns true when the space cannot be loaded for this user and the page should
 * redirect to /settings with replace:true. Gated on !isScopeLoading to prevent
 * bouncing normal space admins on first render (disabled queries have isLoading === false).
 *
 * Note: isScopeChangeSuppressed is part of SpaceDetailState but has no effect here —
 * unavailability is never suppressed by the skip ref.
 */
export function resolveIsSpaceUnavailable(state: SpaceDetailState): boolean {
  const { canRenderSpaceDetail, hasSpace, hasSpaceError, isScopeLoading, isSiteOnlyScoped, isSpaceLoading } = state;
  if (!canRenderSpaceDetail || isScopeLoading) return false;
  return isSiteOnlyScoped || (!isSpaceLoading && (hasSpaceError || !hasSpace));
}

/**
 * Pure decision function for space detail page navigation. Combines the two navigation effects
 * previously in space-detail-page.tsx into a single, testable resolver.
 *
 * Priority:
 * 1. Scope-change branches (only when !isScopeChangeSuppressed):
 *    a. master scope cleared → settings(replace:false)
 *    b. different space selected → site (if masterSiteId) or space+tabSuffix
 *    c. same space + masterSiteId → site
 * 2. Space unavailability → settings(replace:true)
 * 3. null (stay put)
 */
export function resolveSpaceDetailNavigation(state: SpaceDetailState): SpaceDetailNavigationTarget | null {
  const { isScopeChangeSuppressed, masterSiteId, masterSpaceId, routeSpaceId, tabSuffix } = state;

  if (!isScopeChangeSuppressed) {
    if (!masterSpaceId) return { replace: false, route: 'settings' };
    if (masterSpaceId !== routeSpaceId) {
      return resolveScopeChangedTarget(masterSpaceId, masterSiteId, tabSuffix);
    }
    if (masterSiteId) return { route: 'site', siteId: masterSiteId };
  }

  if (resolveIsSpaceUnavailable(state)) return { replace: true, route: 'settings' };
  return null;
}

// ─── Private helpers ──────────────────────────────────────────────────────────

/**
 * Returns the navigation target for a site-only-scoped user. Returns the first site
 * (when available and permitted) or null — never falls through to space detail.
 */
function resolveSiteOnlyNavigationTarget(
  firstSiteId: string | null,
  canEnterSiteSettings: boolean,
): SettingsNavigationTarget | null {
  if (firstSiteId !== null && canEnterSiteSettings) {
    return { route: 'site', siteId: firstSiteId };
  }
  return null;
}

/**
 * Returns the navigation target when the master spaceId differs from the route spaceId.
 * Prefers navigating to the masterSiteId when one is present (user picked a site from a
 * different space via the selector).
 */
function resolveScopeChangedTarget(
  masterSpaceId: string,
  masterSiteId: string | null,
  tabSuffix: string,
): SpaceDetailNavigationTarget {
  if (masterSiteId) return { route: 'site', siteId: masterSiteId };
  return { route: 'space', spaceId: masterSpaceId, tabSuffix };
}
